import QRCodeStyling from "qr-code-styling";
import logo from "../assets/logo-icon.svg";

/**
 * Creates a configured QRCodeStyling instance.
 *
 * @param {number} size - The size of the QR code in pixels.
 * @param {string} eventUrl - The data/URL to encode.
 * @param {string|null} [altLogo=null] - Optional URL for a custom logo.
 *
 * @warning If you are fetching the image for `altLogo` from an external domain
 * in a regular `<img>` tag before this QR code is generated, that initial request
 * MUST include `crossOrigin="anonymous"`. Otherwise, the browser will cache the image
 * without CORS headers, causing a cache poisoning issue and a CORS error when this
 * function attempts to render the image on the canvas.
 *
 * @returns {QRCodeStyling} The configured QR code instance.
 */
export const createQrCode = (size, eventUrl, altLogo = null) => {
  const imageUrl = altLogo || logo;

  const ALT_LOGO_URL = altLogo ? new URL(altLogo) : null;

  // Per qr-code-styling docs, crossOrigin should be set to the origin of the image
  // to avoid CORS issues when using a custom logo
  const crossOrigin = altLogo ? ALT_LOGO_URL.origin : "anonymous";

  return new QRCodeStyling({
    width: size,
    height: size,
    data: eventUrl,
    image: imageUrl,
    dotsOptions: {
      color: "#2b3846",
      type: "dots",
    },
    cornersSquareOptions: {
      color: "#2b3846",
      type: "dot",
    },
    cornersDotOptions: {
      color: "#E53D51",
      type: "dot",
    },
    backgroundOptions: {
      color: "#ffffff",
    },
    imageOptions: {
      crossOrigin,
      margin: 8,
      imageSize: 0.4,
    },
  });
};

/**
 * Fetches an image and returns a local blob URL. This is useful for bypassing
 * browser cache poisoning issues when rendering images on a canvas (like QR codes)
 * that require CORS headers.
 *
 * Also this will help us to to fallback to the default logo if the image is not
 * available.
 *
 * @param {string} url - The URL of the image to fetch.
 * @returns {Promise<string|null>} A promise that resolves to the blob URL, or null if it fails.
 */
export const fetchImageAsBlobUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, {
      mode: "cors",
      cache: "reload",
    });
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Failed to fetch image as blob:", error);
    return null;
  }
};
